import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../hooks";
import { useContext, useEffect, useMemo, useState } from "react";
import { BasicSelector, MwSelector } from "../ui";
import { setLang } from "../../store/langSlice";
import { setisLogging } from "../../store/authSlice";
import axios from "axios";
import periodsContext from "../../utils/TimeMangeHelper";

const resolvePeriodClosed = (period) => {
  const rawStatus = period?.is_close ?? period?.is_closed;

  if (rawStatus !== undefined && rawStatus !== null && rawStatus !== "") {
    if (typeof rawStatus === "string") {
      return rawStatus === "1" || rawStatus.toLowerCase() === "true";
    }
    return rawStatus === 1 || rawStatus === true;
  }

  const closedUntil = String(period?.closeduntil || "").toLowerCase();
  return closedUntil === "closed";
};

const UserBar = ({ tenantUsername, tenantImage, tenant }) => {
  const [t, i18n] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUser } = useUser();

  const _token = localStorage.getItem("token");

  const [openListLang, setOpenListLang] = useState(false);
  const [openListUser, setOpenListUser] = useState(false);

  const langSelected = useSelector((state) => state.lang);
  const user = useSelector((state) => state.auth).user;
  const currentCompany = useSelector((state) => state.currentCompany);
  const currentBranch = useSelector((state) => state.currentBranch);

  const {
    periods,
    loading: periodsLoading,
    fetched,
  } = useContext(periodsContext);

  const [selectedPeriod, setSelectedPeriod] = useState({
    value: null,
    label: null,
    state: null,
    is_current: null,
    closeduntil: null,
    is_closed: null,
    fin_status: null,
    startDateDisplay: null,
    endDateDisplay: null,
  });

  const [showClosedYearAlert, setShowClosedYearAlert] = useState(false);

  const financialPeriodsPath = `/${tenantUsername}/timeMange`;

  const safeParse = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const periodOptions = useMemo(() => {
    const toTime = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const timestamp = Date.parse(String(value));
      return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
    };

    return [...(periods || [])]
      .sort((a, b) => {
        const aTime = toTime(a?.startDateDisplay || a?.fin_start_date);
        const bTime = toTime(b?.startDateDisplay || b?.fin_start_date);
        return aTime - bTime;
      })
      .map((period) => {
        const periodState = String(period?.state || "").toLowerCase();
        const isClosed = resolvePeriodClosed(period);
        const isActive = periodState === "active";
        let optionClassName = "!bg-transparent hover:!bg-transparent";
        let labelClassName = "text-white";

        if (isClosed) {
          optionClassName += " text-slate-400 hover:!text-slate-400";
          labelClassName = "!text-white";
        } else if (isActive) {
          optionClassName +=
            " text-emerald-600 font-bold hover:!text-emerald-600";
          labelClassName = "!text-white !font-bold";
        } else {
          optionClassName += " text-slate-700 hover:!text-slate-700";
          labelClassName = "!text-white";
        }

        return {
          ...period,
          label: `${period.startDateDisplay || period.fin_start_date} - ${
            period.endDateDisplay || period.fin_end_date
          }`,
          value: String(period.id),
          lang: String(langSelected?.value || i18n.language || "en"),
          is_closed: isClosed,
          optionClassName,
          labelClassName,
        };
      });
  }, [periods, langSelected?.value, i18n.language]);

  useEffect(() => {
    const stored = safeParse(localStorage.getItem("selectedPeriod"));
    if (stored?.id) {
      setSelectedPeriod({
        value: stored.id,
        label: stored.label,
        state: stored.state,
        is_current: stored.is_current,
        closeduntil: stored.closeduntil,
        is_closed: stored.is_closed,
        fin_status: stored.fin_status,
        startDateDisplay: stored.startDateDisplay,
        endDateDisplay: stored.endDateDisplay,
      });
    }
  }, []);

  useEffect(() => {
    if (!fetched) return;

    if (!periodOptions.length) {
      if (periodsLoading) return;

      setSelectedPeriod(null);
      localStorage.removeItem("selectedPeriod");
      return;
    }

    const stored = safeParse(localStorage.getItem("selectedPeriod"));

    // Priority: 1) Current/default period, 2) Stored ID, 3) Active state, 4) First one
    const matchedSelected =
      periodOptions.find((p) => p.is_current === 1 || p.is_current === true) ||
      periodOptions.find((p) => String(p.value) === String(stored?.id)) ||
      periodOptions.find((p) => p.state === "active") ||
      periodOptions[0];

    if (matchedSelected) {
      setSelectedPeriod(matchedSelected);
    }
  }, [periodOptions, fetched, periodsLoading, periods]);

  useEffect(() => {
    if (!selectedPeriod?.value) return;

    localStorage.setItem(
      "selectedPeriod",
      JSON.stringify({
        id: selectedPeriod.value,
        label: selectedPeriod.label,
        state: selectedPeriod.state,
        is_current: selectedPeriod.is_current,
        closeduntil: selectedPeriod.closeduntil,
        is_closed: selectedPeriod.is_closed,
        fin_status: selectedPeriod.fin_status,
        startDateDisplay: selectedPeriod.startDateDisplay,
        endDateDisplay: selectedPeriod.endDateDisplay,
      }),
    ); // إطلاق event لتنبيه الصفحات الأخرى بتغيير الفترة
    window.dispatchEvent(new Event("periodChanged"));
    window.dispatchEvent(new Event("periodChanged"));
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod && resolvePeriodClosed(selectedPeriod)) {
      setShowClosedYearAlert(true);
    } else {
      setShowClosedYearAlert(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (!fetched) return;
    if (periodsLoading) return;
    if (periodOptions.length > 0) return;
    if (!tenantUsername) return;
    if (location.pathname === financialPeriodsPath) return;

    navigate(financialPeriodsPath, {
      replace: true,
      state: {
        openAddPeriod: true,
        periodToastMessage: t("times.noPeriodsRedirect"),
      },
    });
  }, [
    fetched,
    periodsLoading,
    periodOptions.length,
    location.pathname,
    navigate,
    tenantUsername,
    t,
  ]);

  const listLangHandler =
    tenant?.languages?.map((language) => ({
      label:
        language.langCode === "ar"
          ? "عربي"
          : language.langCode === "en"
            ? "English"
            : language.langName || language.langCode,
      value: language.langCode,
    })) || [];

  const logoutHandler = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("tenant");

    dispatch(setisLogging({ user: null, isLogging: false }));

    window.location.replace(process.env.REACT_APP_Auth_WEB_BASE_URL);
  };

  const userList = [
    { label: "My Account", value: "MyAccount" },
    { label: "Change Password", value: "ChangePassword" },
    { label: "Logout", value: "Logout" },
  ];

  const onSelectedUserList = (item) => {
    if (item.value === "Logout") logoutHandler();
  };

  const saveCentralSelection = async (
    selectedCompanyId,
    selectedBranchId,
    selectedLang,
  ) => {
    if (!user?.tenant_id) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`,
        {
          tenantId: user.tenant_id,
          scope: "header-selection",
          data: {
            selectedCompanyId,
            selectedBranchId,
            selectedLang,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: () => true,
        },
      );
    } catch (error) {}
  };

  const onSelectedLangHandler = (lang) => {
    i18n.changeLanguage(lang.value);

    dispatch(setLang({ label: lang.label, value: lang.value }));

    saveCentralSelection(
      currentCompany?.value || user?.company_id,
      currentBranch?.value || user?.branch_id,
      lang.value,
    );
  };

  const headerMwSelectorClassName = `h-8 rounded-full border-white/15 bg-white/10 px-3 text-[13px] font-normal  shadow-none backdrop-blur-sm transition-colors hover:bg-white/15 [&>span]:text-white [&>svg]:text-white/70`;

  return (
    <>
      {showClosedYearAlert ? (
        <div
          className={`absolute flex justify-center items-center  gap-2 bottom-[0px]  ${i18n.language === "ar" ? "start-[1.5%]" : "start-[12%]"}  text-[10px] -translate-x-1/2 z-50 text-red-500 font-bold text-center `}
        >
          {t("times.closedYearWarning")}
        </div>
      ) : (
        ""
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs md:justify-end md:me-[-21px] w-full px-2">
        <BasicSelector
          border={false}
          listItem={userList}
          label={getUser?.name}
          defaultValue={getUser?.name}
          openList={openListUser}
          setOpenList={setOpenListUser}
          withChangeLabel={false}
          onSelected={onSelectedUserList}
        >
          {tenantImage ? (
            <img className="w-5 me-3 h-5" src={tenantImage} alt="User Avatar" />
          ) : (
            <img
              className="w-5 me-1 h-5"
              src="/assets/icons/avatar-3637425_640.png"
              alt="User Avatar"
            />
          )}
        </BasicSelector>

        <img
          className="w-4 hover:w-[18.5px] cursor-pointer transition-[0.7s]"
          src="/assets/icons/notification.png"
          alt="Notifications"
        />

        <Link
          className="px-1"
          target="_blank"
          to={`https://support.absat-erp.com/${tenantUsername}/token/${_token}`}
        >
          <img
            className="w-5 hover:w-[21.5px] transition-[0.7s]"
            src="/assets/icons/(4).png"
            alt="Help"
          />
        </Link>

        <div className="relative">
          <MwSelector
            className={`mt-0 py-0 w-[200px] min-w-[190px]  `}
            classnameSelect={headerMwSelectorClassName}
            listClassName="!bg-white"
            _data={periodOptions}
            dataType={"labelValue"}
            selectedItem={selectedPeriod}
            setSelectedItem={setSelectedPeriod}
            initalValue={t("common.Selected_Period")}
            withLabel={false}
            withSearch={false}
            withAddNew={false}
          />
        </div>

        <BasicSelector
          border={false}
          label={langSelected.label}
          openList={openListLang}
          setOpenList={setOpenListLang}
          onSelected={onSelectedLangHandler}
          defaultValue={langSelected.value}
          listItem={listLangHandler}
        >
          <img
            className="w-4 me-2"
            src="/assets/icons/(18).png"
            alt="Language"
          />
        </BasicSelector>
      </div>
    </>
  );
};

export default UserBar;
